import React, {useEffect, useState} from "react";
import {Alert, Button, Card, Col, Divider, Form, Input, InputNumber, message, Row, Select, Typography} from "antd";
import {PlusOutlined} from "@ant-design/icons";
import Layout from "../Layout";

const {Title, Text} = Typography;
const {TextArea} = Input;
const {Option} = Select;

const CreateProductPage = () => {
    const [form] = Form.useForm();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const accessToken = localStorage.getItem("accessToken");

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await fetch("https://api.21stcenturyav.info/categories", {
                    headers: {Authorization: `Bearer ${accessToken}`},
                });
                if (!response.ok) throw new Error("Failed to fetch categories");
                const data = await response.json();
                setCategories(data.categories || [] );
            } catch (error) {
                console.error("Error fetching categories:", error);
                message.error("Error loading categories");
            }
        };

        fetchCategories();
    }, [accessToken]);

    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            console.log("Category:",values.category);
            const response = await fetch("https://api.21stcenturyav.info/product", {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    name: values.name,
                    sku: values.sku,
                    price: values.price,
                    category: values.category,
                    stock_quantity: values.stock_quantity,
                    brand: values.brand,
                    gtin: values.gtin,
                    focus_keyword: values.focus_keyword,
                    description: values.description,
                    short_description: values.short_description,
                    tags: values.tags,
                }),
            });
            if (!response.ok) throw new Error("Failed to create product");

            message.success("Product created successfully");
            form.resetFields();
        } catch (error) {
            console.error("Error creating product:", error);
            message.error("Error creating product");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout pageTitle="Create New Product">
            <div style={{padding: "20px", maxWidth: "800px", margin: "0 auto"}}>
                <Card
                    title={
                        <Title level={3} style={{margin: 0}}>
                            <PlusOutlined style={{marginRight: 12, color: "#1890ff"}}/>
                            Create New Product
                        </Title>
                    }
                    bordered={false}
                    headStyle={{border: 'none'}}
                >
                    <Alert
                        message="All fields are required"
                        type="info"
                        showIcon
                        style={{marginBottom: 24}}
                    />

                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                        initialValues={{
                            stock_quantity: 1,
                            price: 0.0,
                        }}
                    >
                        <Divider orientation="left" orientationMargin="0">
                            <Text strong style={{color: "#1890ff"}}>Basic Information</Text>
                        </Divider>

                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item
                                    label={<Text strong>Product Name</Text>}
                                    name="name"
                                    rules={[{required: true, message: "Please enter product name"}]}
                                >
                                    <Input
                                        placeholder="e.g., Gaming Mouse Pad Pro X200"
                                        size="large"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label={<Text strong>SKU</Text>}
                                    name="sku"
                                    rules={[{required: true, message: "Please enter SKU"}]}
                                >
                                    <Input
                                        placeholder="e.g., GMP-PRO-X200"
                                        size="large"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label={<Text strong>Category</Text>}
                                    name="category"
                                    rules={[{required: true, message: "Please select a category"}]}
                                >
                                    <Select
                                        size="large"
                                        placeholder="Select category"
                                        showSearch
                                        optionFilterProp="children"
                                    >
                                        {categories.map((category) => (
                                            <Option key={category.id} value={category.name}>
                                                {category.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider orientation="left" orientationMargin="0">
                            <Text strong style={{color: "#1890ff"}}>Pricing & Inventory</Text>
                        </Divider>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label={<Text strong>Price (£)</Text>}
                                    name="price"
                                    rules={[{required: true, message: "Please enter price"}]}
                                >
                                    <InputNumber
                                        min={0}
                                        step={0.01}
                                        style={{width: "100%"}}
                                        size="large"
                                        formatter={(value) => `£${value}`}
                                        parser={(value) => value.replace(/£\s?|(,*)/g, "")}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label={<Text strong>Stock Quantity</Text>}
                                    name="stock_quantity"
                                    rules={[{required: true, message: "Please enter stock quantity"}]}
                                >
                                    <InputNumber
                                        min={0}
                                        style={{width: "100%"}}
                                        size="large"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider orientation="left" orientationMargin="0">
                            <Text strong style={{color: "#1890ff"}}>Product Details</Text>
                        </Divider>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label={<Text strong>Brand</Text>}
                                    name="brand"
                                    rules={[{required: true, message: "Please enter brand name"}]}
                                >
                                    <Input
                                        placeholder="e.g., GameStar"
                                        size="large"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label={<Text strong>GTIN</Text>}
                                    name="gtin"
                                    rules={[{required: true, message: "Please enter GTIN"}]}
                                >
                                    <Input
                                        placeholder="e.g., 1234567890123"
                                        size="large"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item
                            label={<Text strong>Focus Keyword</Text>}
                            name="focus_keyword"
                            rules={[{required: true, message: "Please enter focus keyword"}]}
                        >
                            <Input
                                placeholder="e.g., gaming accessories"
                                size="large"
                            />
                        </Form.Item>

                        <Form.Item
                            label={<Text strong>Description</Text>}
                            name="description"
                            rules={[{required: true, message: "Please enter description"}]}
                        >
                            <TextArea
                                rows={4}
                                placeholder="Detailed product description..."
                                size="large"
                            />
                        </Form.Item>

                        <Form.Item
                            label={<Text strong>Short Description</Text>}
                            name="short_description"
                            rules={[{required: true, message: "Please enter short description"}]}
                        >
                            <TextArea
                                rows={2}
                                placeholder="Brief product summary..."
                                size="large"
                                maxLength={160}
                                showCount
                            />
                        </Form.Item>

                        <Form.Item
                            label={<Text strong>Tags</Text>}
                            name="tags"
                            rules={[{required: true, message: "Please enter tags"}]}
                            extra="Separate tags with commas"
                        >
                            <Input
                                placeholder="e.g., gaming, mouse pad, accessories"
                                size="large"
                            />
                        </Form.Item>

                        <Form.Item>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                size="large"
                                block
                                style={{height: 48, fontWeight: 600}}
                            >
                                {loading ? 'Creating Product...' : 'Create Product'}
                            </Button>
                        </Form.Item>
                    </Form>
                </Card>
            </div>
        </Layout>
    );
};

export default CreateProductPage;